import Icon from "components/Icons/Icon";
import { useAppDownloadUrl } from "components/common/useAppStoreLink";
import trackEvent from "components/tracking/trackEvent";
import trackFathom from "components/tracking/trackFathom";
import { PlanGateModal, usePlanGate } from "components/website/components/planGate";
import { useState } from "react";
import { OsTypes, isMobile, osName } from "react-device-detect";
import useReactRouter from "use-react-router";
import { t } from "i18n/index";
import DownloadAppModal from "../components/DownloadAppModal";
import DownloadAppQrCard from "../components/DownloadAppQrCard";
import Footer from "../components/Footer";
import HowItWorksSection from "../components/HowItWorksSection";
import Navigation from "../components/navigation/Navigation";
import LanguageSwitcher from "../components/LanguageSwitcher";
import "../css/Photobook.scss";
import "../css/Website.scss";
import Photobook1 from "../img/screenshots/photobook/Photobook1.png";
import Photobook2 from "../img/screenshots/photobook/Photobook2.png";
import { PHOTOBOOK_CONTENT } from "../marketing/content/featurePageContent";
import { getPhotobookTranslations } from "../marketing/i18n/pages/photobook";

interface PhotobookPageProps {
	lang?: string;
	uiTranslations?: Record<string, string>;
}

const STIPPL_PATH =
	"M17.3912 99.1864C14.9217 99.1864 12.9613 101.159 13.0092 103.593C13.0589 106.025 15.0991 108 17.5668 108C20.0363 108 21.9967 106.025 21.9488 103.593C21.8991 101.159 19.8589 99.1864 17.3912 99.1864ZM65.3336 33.3614C76.1235 30.4927 80.4221 24.1043 80.431 16.7419C80.4434 6.75566 72.3519 0 59.0871 0C38.1939 0 0 22.4846 0 43.1187C0 59.3036 21.0387 64.5548 35.3004 67.8972C49.5639 71.2377 60.588 74.2732 60.588 78.2187C60.588 82.1625 48.0081 91.0523 27.7464 97.4815C25.575 98.2036 24.3065 100.144 24.9097 101.816C25.5147 103.487 27.7145 104.079 29.9356 103.533C38.3749 101.452 78.201 92.3474 78.201 74.4204C78.201 56.4935 35.2862 49.1612 35.2862 45.9005C35.2862 42.5528 56.6017 35.6819 65.3336 33.3614Z";

const PRICE_STEPS = [25, 50, 75, 100, 150, 200, 250, 300, 350, 400];
const PRICES_STANDARD = [32.5, 45, 57.5, 70, 95, 112.5, 130, 147.5, 165, 182.5];

function getPageLabels(translations: Record<string, string>): string[] {
	const compact = t(translations, "photobook.pricing.pageLabel.compact", "Compact · perfect for a weekend trip");
	const standardShort = t(translations, "photobook.pricing.pageLabel.standardShort", "Standard · ideal for a 1–2 week trip");
	const standardLong = t(translations, "photobook.pricing.pageLabel.standardLong", "Standard · ideal for a 2–3 week trip");
	const extendedShort = t(translations, "photobook.pricing.pageLabel.extendedShort", "Extended · great for a month-long trip");
	const extendedLong = t(translations, "photobook.pricing.pageLabel.extendedLong", "Extended · for longer adventures");
	const large = t(translations, "photobook.pricing.pageLabel.large", "Large · serious traveller");
	const xl = t(translations, "photobook.pricing.pageLabel.xl", "XL · the full epic journey");
	return [compact, standardShort, standardLong, extendedShort, extendedLong, large, large, xl, xl, xl];
}

function getInsideCards(translations: Record<string, string>) {
	return [
		{
			num: "01",
			title: t(translations, "photobook.inside.card1.title", "Full-bleed photo cover"),
			desc: t(
				translations,
				"photobook.inside.card1.desc",
				"Your favourite trip photo becomes the cover, with your trip name and year printed in clean typography. Hardcover quality."
			),
		},
		{
			num: "02",
			title: t(translations, "photobook.inside.card2.title", "Trip summary spread"),
			desc: t(
				translations,
				"photobook.inside.card2.desc",
				"Your travel stats at a glance: destinations, days, photos and countries. The numbers that define your journey."
			),
		},
		{
			num: "03",
			title: t(translations, "photobook.inside.card3.title", "Route map"),
			desc: t(
				translations,
				"photobook.inside.card3.desc",
				"Your actual route: every destination connected in the order you visited them, shown on a beautiful satellite map. Only possible because Stippl tracked your whole journey."
			),
		},
		{
			num: "04",
			title: t(translations, "photobook.inside.card4.title", "Day-by-day photo spreads"),
			desc: t(
				translations,
				"photobook.inside.card4.desc",
				"Full-bleed photography for each leg of your trip, pulled directly from your journal entries. Each day gets its own visual space."
			),
		},
		{
			num: "05",
			title: t(translations, "photobook.inside.card5.title", "Your journal entries"),
			desc: t(
				translations,
				"photobook.inside.card5.desc",
				"The words you wrote in the moment appear alongside the photos: your impressions, your reflections, the things you noticed. Not captions you had to add later."
			),
		},
		{
			num: "06",
			title: t(translations, "photobook.inside.card6.title", "Destination highlights"),
			desc: t(
				translations,
				"photobook.inside.card6.desc",
				"Each country and city gets its own section: places you saved, things you did, where you stayed. Your trip, organised the way you actually experienced it."
			),
		},
	];
}

function getQualityCards(translations: Record<string, string>) {
	return [
		{
			iconName: "camera--outlined",
			iconClass: "qci-purple",
			iconFill: "#7c3aed",
			title: t(translations, "photobook.quality.card1.title", "Full-bleed photo printing"),
			desc: t(translations, "photobook.quality.card1.desc", "Your photos fill the page edge to edge: no white borders, no compression. Exactly the way you shot them."),
		},
		{
			iconName: "article",
			iconClass: "qci-teal",
			iconFill: "#0f3e4a",
			title: t(translations, "photobook.quality.card2.title", "Premium cover & binding"),
			desc: t(translations, "photobook.quality.card2.desc", "Hardcover quality with your trip photo on the front. Built to sit on a bookshelf and last for years."),
		},
		{
			iconName: "culture",
			iconClass: "qci-green",
			iconFill: "var(--color-primary)",
			title: t(translations, "photobook.quality.card3.title", "Editorial layout, auto-generated"),
			desc: t(
				translations,
				"photobook.quality.card3.desc",
				"Clean typographic design generated from your journal: no blank templates, no manual placement. Every book unique."
			),
		},
		{
			iconName: "globe",
			iconClass: "qci-purple",
			iconFill: "#7c3aed",
			title: t(translations, "photobook.quality.card4.title", "Satellite route map inside"),
			desc: t(
				translations,
				"photobook.quality.card4.desc",
				"A full-spread satellite map of your journey, showing every destination in the order you visited. Exclusive to Stippl."
			),
		},
	];
}

function getIncludesItems(translations: Record<string, string>): string[] {
	return [
		t(translations, "photobook.pricing.includesItem1", "Hardcover binding"),
		t(translations, "photobook.pricing.includesItem2", "Full-bleed photo cover"),
		t(translations, "photobook.pricing.includesItem3", "Trip summary spread"),
		t(translations, "photobook.pricing.includesItem4", "Route map from your itinerary"),
		t(translations, "photobook.pricing.includesItem5", "Day-by-day photo spreads"),
		t(translations, "photobook.pricing.includesItem6", "Journal entries as page text"),
		t(translations, "photobook.pricing.includesItem7", "Destination highlights"),
		t(translations, "photobook.pricing.includesItem8", "Professional print quality"),
	];
}

function getFaqItems(translations: Record<string, string>) {
	return [
		{
			q: t(translations, "photobook.faq.q1.question", "What is a travel photobook?"),
			a: (
				<>
					<strong>
						{t(
							translations,
							"photobook.faq.q1.highlight",
							"A travel photobook is a printed book made from your trip photos, journal entries and memories, organised by day and destination."
						)}
					</strong>{" "}
					{t(
						translations,
						"photobook.faq.q1.body",
						"The best travel photobooks go beyond a photo album: they include your travel route, written reflections and trip statistics. Stippl’s photobook is unique because it’s built from your travel journal, everything is already organised before you open the photobook creator. Photos are sorted by day, your notes become the captions, and your actual GPS route appears as a satellite map inside the book."
					)}
				</>
			),
		},
		{
			q: t(translations, "photobook.faq.q2.question", "Do I need Stippl Pro to create a photobook?"),
			a: (
				<>
					<strong>{t(translations, "photobook.faq.q2.highlight", "No, photobooks are available to all Stippl users.")}</strong>{" "}
					{t(
						translations,
						"photobook.faq.q2.body",
						"You don’t need a Pro subscription to create or order a photobook. Stippl Pro members (from €4.99/month) receive a 20% discount on every photobook order. Pro also includes unlimited AI trip generation, PDF export, calendar sync and more."
					)}
				</>
			),
		},
		{
			q: t(translations, "photobook.faq.q3.question", "How long does it take to create a photobook?"),
			a: (
				<>
					<strong>{t(translations, "photobook.faq.q3.highlight", "Under 5 minutes, if your journal is already up to date.")}</strong>{" "}
					{t(
						translations,
						"photobook.faq.q3.body",
						"That’s the point: because you journalled and tracked your route as you travelled, the hard work is already done by the time you get home. Open your journal, tap “Create photobook”, choose your content, and Stippl generates the layout automatically. The more you put into your journal on the road, the richer the book becomes."
					)}
				</>
			),
		},
	];
}

function FaqAccordion({ faqItems }: { faqItems: { q: string; a: JSX.Element }[] }) {
	const [openIdx, setOpenIdx] = useState(0);
	return (
		<div className="faq-list">
			{faqItems.map((item, i) => (
				<div className={`faq-item${openIdx === i ? " faq-item--open" : ""}`} key={i}>
					<div className="faq-question" onClick={() => setOpenIdx(openIdx === i ? -1 : i)}>
						<span>{item.q}</span>
						<div className="faq-toggle" />
					</div>
					{openIdx === i && <div className="faq-answer">{item.a}</div>}
				</div>
			))}
		</div>
	);
}

export default function PhotobookPage({ lang }: PhotobookPageProps) {
	const { match } = useReactRouter();
	const translations = getPhotobookTranslations(lang || "en");

	const PAGE_LABELS = getPageLabels(translations);
	const INSIDE_CARDS = getInsideCards(translations);
	const QUALITY_CARDS = getQualityCards(translations);
	const INCLUDES_ITEMS = getIncludesItems(translations);
	const FAQ_ITEMS = getFaqItems(translations);
	const [showDownloadModal, setShowDownloadModal] = useState(false);
	// 123yb691qz1: every /register anchor on this page opens the signup modal in place instead
	// of navigating; `mobile: "store"` keeps 123yb691bdn's rule that a phone goes to the store
	// instead. `match.path` reuses the label `handleTrack("signup")` already tracked under.
	const gate = usePlanGate(match.path, "store");

	const handleTrack = (type: "signup" | "download" | "appstore" | "googleplay") => {
		if (type === "appstore") {
			trackFathom("FD21GVOP");
			trackEvent("click_appstore_cta", { path: match.path }, "marketing_site");
		} else if (type === "googleplay") {
			trackFathom("BNQKH0MH");
			trackEvent("click_googleplay_cta", { path: match.path }, "marketing_site");
		} else if (osName === OsTypes.Android && type === "download") {
			trackFathom("BNQKH0MH");
			trackEvent("click_googleplay_cta", { path: match.path }, "marketing_site");
		} else if (osName === OsTypes.IOS && type === "download") {
			trackFathom("FD21GVOP");
			trackEvent("click_appstore_cta", { path: match.path }, "marketing_site");
		} else {
			trackEvent("click_signup_cta", { path: match.path }, "marketing_site");
			trackFathom("E4NT4HWN");
		}
	};

	// 123yb6937h3: both "Download the app" CTAs below resolve their href through this -- always a
	// real, attributed OneLink, including on the very first render before mount (see
	// useAppDownloadUrl's doc comment in useAppStoreLink.ts). Previously these were <button
	// onClick={setShowDownloadModal(true)}> with no href at all, so the server HTML had a dead
	// control -- confirmed on production by QA's curl retest of 123yb6937h3.
	const appDownloadUrl = useAppDownloadUrl();

	const [sliderIndex, setSliderIndex] = useState(1);
	const pages = PRICE_STEPS[sliderIndex];
	const priceStandard = PRICES_STANDARD[sliderIndex];
	const pricePro = Math.round(priceStandard * 0.8 * 100) / 100;
	const saving = Math.round((priceStandard - pricePro) * 100) / 100;
	const sliderPct = (sliderIndex / (PRICE_STEPS.length - 1)) * 100;

	return (
		<div className="photobook-page" id="stippl-photobook">
			<Navigation impact={true} langMenu={<LanguageSwitcher type="page" />} />

			{/* HERO */}
			<section className="hr-hero">
				<div className="hero-bg" />
				<svg className="hero-smark" width="560" height="748" viewBox="0 0 81 108" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d={STIPPL_PATH} fill="white" />
				</svg>
				<svg className="hero-smark-left" width="280" height="374" viewBox="0 0 81 108" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d={STIPPL_PATH} fill="white" />
				</svg>
				<div className="container__inner hr-hero__inner">
					<div className="hr-hero__content">
						<div className="hero-chip">{t(translations, "photobook.hero.chip", "Travel Photobook")}</div>
						<h1>
							{t(translations, "photobook.hero.title.line1", "Your trip.")}
							<br />
							<span className="accent">{t(translations, "photobook.hero.title.line2", "Printed.")}</span>
							<br />
							{t(translations, "photobook.hero.title.line3", "Beautifully.")}
						</h1>
						<p className="hr-hero__desc">
							{t(
								translations,
								"photobook.hero.desc",
								"Journal your trip as you go. Get home. Hold your journey in your hands: photos, route map and all, in a high-quality printed travel photo book."
							)}
						</p>
						<div className="hero-ctas">
							{/* 123yb6937h3: on a phone the anchor is simply followed -- OneLink does the OS split
								(App Store vs Play Store) server-side, which is the ticket's acceptance criterion.
								On desktop we still want DownloadAppModal's QR code as the affordance, so the click
								handler -- browser-only, unlike a render-time device check -- prevents the default
								navigation and opens the modal instead. */}
							<a
								href={appDownloadUrl}
								className="hero-glass-btn"
								onClick={(e) => {
									handleTrack("download");
									if (!isMobile) {
										e.preventDefault();
										setShowDownloadModal(true);
									}
								}}
							>
								<span className="hero-glass-btn__icon">
									<svg width="11" height="15" viewBox="0 0 81 108" fill="none" xmlns="http://www.w3.org/2000/svg">
										<path d={STIPPL_PATH} fill="#fff" />
									</svg>
								</span>
								<span className="hero-glass-btn__label">{t(translations, "photobook.hero.downloadApp", "Download the app")}</span>
							</a>
							{/* "Start on web" returns when the web editor ships: work/web-photobook-editor.md in the stippl-knowledge repo. */}
						</div>
					</div>
					<img
						className="hr-hero__phone hr-hero__phone--front"
						src={Photobook1}
						alt={t(translations, "photobook.hero.imgAlt", "Stippl photobook preview")}
						loading="eager"
					/>
				</div>
			</section>

			{/* INTRO */}
			<section className="intro-section">
				<div className="intro-inner">
					<div className="section-eyebrow" style={{ justifyContent: "center", marginBottom: 16 }}>
						<span className="chip chip-filled-purple">{t(translations, "photobook.intro.chip", "Your travel journal, beautifully printed")}</span>
					</div>
					<h2>
						{t(translations, "photobook.intro.title.line1", "Your travel journal, turned into")}
						<br />
						{t(translations, "photobook.intro.title.article", "a")} <em>{t(translations, "photobook.intro.title.emphasis", "printed travel photobook.")}</em>
					</h2>
					<p>
						{t(
							translations,
							"photobook.intro.body",
							"You plan your journey, track your route, journal your days and capture your memories, all in Stippl. When you get home, your entire trip is already organised and ready to become a book. No manual uploading. No sorting through camera rolls. The hard work is done."
						)}
					</p>
				</div>
			</section>

			{/* WHAT'S INSIDE */}
			<section className="inside-section">
				<div className="inside-inner">
					<div className="inside-header">
						<div className="section-eyebrow" style={{ justifyContent: "center", marginBottom: 16 }}>
							<span className="chip chip-filled-purple">{t(translations, "photobook.inside.chip", "What’s inside")}</span>
						</div>
						<h2>
							{t(translations, "photobook.inside.title.line1", "What’s inside your")}
							<br />
							<span className="accent">{t(translations, "photobook.inside.title.accent", "travel photo book.")}</span>
						</h2>
						<p>{t(translations, "photobook.inside.subtitle", "Not a generic travel album: your actual trip, laid out the way you lived it.")}</p>
					</div>
					<div className="inside-grid">
						{INSIDE_CARDS.map((card) => (
							<div className="inside-card" key={card.num}>
								<div className="inside-card-num">{card.num}</div>
								<h3>{card.title}</h3>
								<p>{card.desc}</p>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* ROUTE MAP FEATURE */}
			<section className="route-section">
				<div className="route-inner">
					<div>
						<div className="section-eyebrow">
							<span className="chip chip-filled-purple">{t(translations, "photobook.route.chip", "The route map")}</span>
						</div>
						<h2 className="feat-h2">
							{t(translations, "photobook.route.title.line1", "Your actual route,")}
							<br />
							{t(translations, "photobook.route.title.line2", "mapped in your travel photo book.")}
						</h2>
						<p className="feat-body">
							{t(
								translations,
								"photobook.route.body1",
								"This is what sets a Stippl photobook apart from every other travel photo book on the market. Because Stippl tracks your route as you travel, your photobook contains a real satellite map of your journey: every destination connected in the order you visited them, with photo bubbles at each stop."
							)}
						</p>
						<p className="feat-body">
							{t(translations, "photobook.route.body2", "Other photobook apps can’t do this. They only know what you upload. Stippl knows where you went.")}
						</p>
						<div className="feat-bullets">
							<div className="feat-bullet">
								<div className="feature-list-icon purple">
									<Icon name="marker--outlined" size={20} fill="var(--main4)" />
								</div>
								<div className="feat-bullet-text">
									<strong>{t(translations, "photobook.route.bullet1.strong", "Your actual route")}</strong>
									{t(translations, "photobook.route.bullet1.rest", ": pulled from your Stippl itinerary, exactly as you planned and travelled it.")}
								</div>
							</div>
							<div className="feat-bullet">
								<div className="feature-list-icon green">
									<Icon name="satellite" size={20} pathProps={{ stroke: "var(--main4)" }} />
								</div>
								<div className="feat-bullet-text">
									<strong>{t(translations, "photobook.route.bullet2.strong", "Satellite map background")}</strong>
									{t(translations, "photobook.route.bullet2.rest", ": your journey shown on a beautiful globe view.")}
								</div>
							</div>
							<div className="feat-bullet">
								<div className="feature-list-icon purple">
									<Icon name="camera--outlined" size={20} fill="var(--main4)" />
								</div>
								<div className="feat-bullet-text">
									<strong>{t(translations, "photobook.route.bullet3.strong", "Photo bubbles at each stop")}</strong>
									{t(translations, "photobook.route.bullet3.rest", ": your faces and moments pinned to the places you were.")}
								</div>
							</div>
						</div>
						<a
							href="/register"
							className="feat-cta-link"
							onClick={(e) => {
								e.preventDefault();
								gate.requestPlan();
							}}
						>
							{t(translations, "photobook.route.cta", "Start tracking your trip →")}
						</a>
					</div>

					<div className="route-illustration">
						<img
							src={Photobook2}
							alt={t(translations, "photobook.route.imgAlt", "Photobook route map spread")}
							className="route-illustration__img"
							loading="lazy"
						/>
					</div>
				</div>
			</section>

			{/* HOW IT WORKS */}
			<HowItWorksSection
				theme="purple"
				tinted
				title={t(translations, "photobook.howToUse.name", PHOTOBOOK_CONTENT.howToUse.name)}
				description={t(translations, "photobook.howToUse.description", PHOTOBOOK_CONTENT.howToUse.description)}
				steps={PHOTOBOOK_CONTENT.howToSteps.map((step, index) => ({
					...step,
					name: t(translations, `photobook.howToSteps.${index}.name`, step.name),
					text: t(translations, `photobook.howToSteps.${index}.text`, step.text),
				}))}
				ctaText={t(translations, "photobook.howItWorks.cta", "Start your travel photobook →")}
				onCtaClick={() => handleTrack("signup")}
			/>

			{/* QUALITY SECTION */}
			<section className="quality-section">
				<div className="quality-inner">
					<div>
						<div className="section-eyebrow">
							<span className="chip chip-filled-purple">{t(translations, "photobook.quality.chip", "Quality")}</span>
							<span className="section-label">{t(translations, "photobook.quality.label", "Worth keeping forever")}</span>
						</div>
						<h2 className="feat-h2">
							{t(translations, "photobook.quality.title.line1", "Not a generic")}
							<br />
							{t(translations, "photobook.quality.title.line2", "holiday photo book.")}
						</h2>
						<p className="feat-body">
							{t(
								translations,
								"photobook.quality.body1",
								"Stippl photobooks are printed to a standard that matches how significant your travels actually are. The cover is full-bleed. The photos are crisp. The binding is built to last. This is a book you’ll put on your shelf, not in a drawer."
							)}
						</p>
						<p className="feat-body">
							{t(
								translations,
								"photobook.quality.body2",
								"The design is generated automatically from your journal: clean, editorial, and completely unique to your trip. No templates. No generic layouts."
							)}
						</p>
					</div>
					<div className="quality-cards">
						{QUALITY_CARDS.map((card, i) => (
							<div className="quality-card" key={i}>
								<div className={`qc-icon ${card.iconClass}`}>
									<Icon name={card.iconName as any} size={20} fill={card.iconFill} />
								</div>
								<div className="qc-text">
									<h4>{card.title}</h4>
									<p>{card.desc}</p>
								</div>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* PRICING */}
			<section className="pricing-section">
				<div className="pricing-inner">
					<div
						style={{
							display: "inline-flex",
							alignItems: "center",
							background: "#9376e8",
							color: "#fff",
							borderRadius: 20,
							padding: "5px 14px",
							fontFamily: "'Rubik', sans-serif",
							fontSize: 13,
							fontWeight: 500,
							marginBottom: 16,
						}}
					>
						{t(translations, "photobook.pricing.badge", "Photobook pricing")}
					</div>
					<h2>
						{t(translations, "photobook.pricing.title.line1", "Pick your size.")}
						<br />
						{t(translations, "photobook.pricing.title.line2", "We’ll handle the rest.")}
					</h2>
					<p>{t(translations, "photobook.pricing.subtitle", "All prices excl. shipping. Pro members save 20% on every order.")}</p>
					<div className="pricing-card">
						<div className="slider-pages-label">{t(translations, "photobook.pricing.pagesLabel", "Number of pages")}</div>
						<div className="slider-pages-num">
							{pages} {t(translations, "photobook.pricing.pagesUnit", "pages")}
						</div>
						<div className="slider-pages-sub">{PAGE_LABELS[sliderIndex]}</div>
						<input
							type="range"
							className="pb-slider"
							min={0}
							max={PRICE_STEPS.length - 1}
							step={1}
							value={sliderIndex}
							onChange={(e) => setSliderIndex(Number(e.target.value))}
							style={{
								backgroundImage: `linear-gradient(to right, #9376e8 ${sliderPct}%, #dde9ed ${sliderPct}%)`,
							}}
						/>
						<div className="slider-range-labels">
							<span>
								25 {t(translations, "photobook.pricing.pagesUnit", "pages")}
							</span>
							<span>
								400 {t(translations, "photobook.pricing.pagesUnit", "pages")}
							</span>
						</div>
						<div className="price-boxes">
							<div className="price-box price-box--standard">
								<div className="price-box-label">{t(translations, "photobook.pricing.standardPrice", "Standard price")}</div>
								<div className="price-box-num">€{priceStandard.toFixed(2)}</div>
								<div className="price-box-note">{t(translations, "photobook.pricing.exclShipping", "excl. shipping")}</div>
							</div>
							<div className="price-box price-box--pro">
								<div className="price-pro-badge">PRO</div>
								<div className="price-box-label price-box-label--pro">{t(translations, "photobook.pricing.proPrice", "Pro price")}</div>
								<div className="price-box-num price-box-num--pro">€{pricePro.toFixed(2)}</div>
								<div className="price-box-saving">
									{t(translations, "photobook.pricing.saveLabel", "save")} €{saving.toFixed(2)} · 20
									{t(translations, "photobook.pricing.percentOffSuffix", "% off")}
								</div>
							</div>
						</div>
						<a
							href="/register"
							className="pricing-cta"
							onClick={(e) => {
								e.preventDefault();
								gate.requestPlan();
							}}
						>
							{t(translations, "photobook.pricing.cta", "Start your journal free")}
						</a>
						<p className="pricing-note">
							{t(translations, "photobook.pricing.note", "Final price depends on exact page count and delivery location. Shipping not included.")}
						</p>
						<div className="includes-block">
							<h4>{t(translations, "photobook.pricing.includesTitle", "Every book includes")}</h4>
							<p>
								{t(
									translations,
									"photobook.pricing.includesDesc",
									"Printed hardcover. Professional binding. All generated automatically from your Stippl journal."
								)}
							</p>
							<div className="includes-grid">
								{INCLUDES_ITEMS.map((item, i) => (
									<div className="includes-item" key={i}>
										<span className="includes-check">✓</span>
										<span>{item}</span>
									</div>
								))}
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* FAQ */}
			<section className="faq-section">
				<div className="faq-inner">
					<div className="faq-header">
						<h2>{t(translations, "photobook.faq.title", "Common questions")}</h2>
						<p>{t(translations, "photobook.faq.subtitle", "Everything you need to know about Stippl travel photobooks.")}</p>
					</div>
					<FaqAccordion faqItems={FAQ_ITEMS} />
				</div>
			</section>

			{/* SUPPORT CARD */}
			<section className="support-section">
				<div className="support-card">
					<div className="support-card-icon">
						<Icon name="chat" size={20} fill="var(--main2)" />
					</div>
					<div className="support-card-text">
						<h4>{t(translations, "photobook.support.title", "Got a question about your photobook?")}</h4>
						<p>{t(translations, "photobook.support.body", "Our support team is happy to help with orders, delivery, content and everything in between.")}</p>
					</div>
					<a href="mailto:support@stippl.io" className="btn-outline-purple">
						{t(translations, "photobook.support.cta", "Contact support →")}
					</a>
				</div>
			</section>

			{/* CTA STRIP */}
			<section className="cta-strip">
				<svg className="cta-s-mark" viewBox="0 0 81 108" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d={STIPPL_PATH} fill="#ffffff" />
				</svg>
				<div className="cta-inner">
					<h2>
						{t(translations, "photobook.cta.title.line1", "Your next trip.")}
						<br />
						{t(translations, "photobook.cta.title.line2Pre", "Your next")} <span className="accent">{t(translations, "photobook.cta.title.accent", "book.")}</span>
					</h2>
					<p>{t(translations, "photobook.cta.body", "Start by journalling your journey in Stippl. The photobook will be waiting when you get home.")}</p>
					<div className="cta-buttons">
						{/* Same fix as the hero CTA above (123yb6937h3): a real href on the server render, store
							link on mobile, modal-with-QR on desktop. */}
						<a
							href={appDownloadUrl}
							className="cta-glass-btn"
							onClick={(e) => {
								handleTrack("download");
								if (!isMobile) {
									e.preventDefault();
									setShowDownloadModal(true);
								}
							}}
						>
							<span className="cta-glass-btn__icon">
								<svg width="16" height="21" viewBox="0 0 81 108" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d={STIPPL_PATH} fill="#fff" />
								</svg>
							</span>
							<span className="cta-glass-btn__label">{t(translations, "photobook.hero.downloadApp", "Download the app")}</span>
						</a>
						{/* "Start on web" returns when the web editor ships, here and in the hero. */}
					</div>
				</div>
			</section>

			<Footer />
			<DownloadAppQrCard />
			<DownloadAppModal isOpen={showDownloadModal} onClose={() => setShowDownloadModal(false)} location={match.path} />
			<PlanGateModal gate={gate} title="Create a free account to start planning" />
		</div>
	);
}
